"use client";

import { Check, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Materialcomparison() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: tableRef,
    isVisible: tableVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const comparisonData = [{
    feature: "Heat Resistance",
    phenolic: "Up to 300°F (Continuous)",
    acrylic: "Up to 160°F (Continuous)"
  }, {
    feature: "Chemical Resistance",
    phenolic: "High (Solvents, Oils, Acids)",
    acrylic: "Moderate"
  }, {
    feature: "UV Stability",
    phenolic: "Excellent (Non-fading)",
    acrylic: "Good (UV stable options)"
  }, {
    feature: "Standard Thickness",
    phenolic: "1/16\", 1/8\"",
    acrylic: "1/16\", 1/8\", 1/4\""
  }, {
    feature: "MIL-SPEC Compliance",
    phenolic: "LP-387A, LP-509",
    acrylic: "Standard Industrial"
  }, {
    feature: "Common Application",
    phenolic: "Critical Infrastructure / Power",
    acrylic: "General Labeling / Office"
  }];
  return <section data-section-id="3547" id="materialcomparison" className="relative bg-[#E8E4DC] overflow-hidden py-24 md:py-32 lg:py-40">
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 -left-40 h-[600px] w-[600px] rounded-lg" style={{
      background: "radial-gradient(circle, hsla(45, 68%, 47%, 0.05) 0%, transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-8">
            <p className="font-mono text-xs tracking-widest text-[#0F1B2D]/60 mb-6 md:mb-8">
              [ 02 / MATERIAL SPECIFICATIONS ]
            </p>
            <h2 className="font-default font-bold text-4xl md:text-6xl lg:text-7xl leading-[1.1] tracking-tight uppercase text-[#0F1B2D]">
              When Should You Use Phenolic vs. Two-Color Acrylic?
            </h2>
          </div>
          <div className="lg:col-span-4 flex items-end">
            <p className="font-default text-lg md:text-xl leading-relaxed text-[#0F1B2D]">Selecting the right substrate is critical for compliance and longevity. For technical guidance on your specific environment, <a data-link-type="route" data-link-href="/ContactPage" className="text-[#0F1B2D] font-bold underline decoration-[#C9A227] hover:text-[#C9A227] transition-colors" data-ast-id="f:/src/pages/Phenolic/sections/Materialcomparison.tsx|sig:8xdqk1" data-ast-start="2578" data-ast-end="2727" href="/ContactPage" contenteditable="false" style={{}}>contact us.</a></p>
          </div>
        </div>

        <div ref={tableRef} data-index={0} className={`mt-24 md:mt-32 overflow-x-auto transition-all duration-700 ease-out delay-150 ${tableVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-[#0F1B2D] text-white">
                <th className="py-6 px-6 font-mono text-sm tracking-widest uppercase border-b-4 border-[#C9A227]">
                  Feature / Specification
                </th>
                <th className="py-6 px-6 font-mono text-sm tracking-widest uppercase border-b-4 border-[#C9A227]">
                  Phenolic (Thermoset)
                </th>
                <th className="py-6 px-6 font-mono text-sm tracking-widest uppercase border-b-4 border-[#C9A227]">
                  Two-Color Acrylic
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#0F1B2D]/10">
              {comparisonData.map((row, i) => <tr key={i} className="hover:bg-[#0F1B2D]/5 transition-colors">
                  <td className="py-5 px-6 font-bold text-[#0F1B2D] bg-[#0F1B2D]/5 lg:bg-transparent">
                    {row.feature}
                  </td>
                  <td className="py-5 px-6 text-[#0F1B2D]">
                    {row.phenolic}
                  </td>
                  <td className="py-5 px-6 text-[#0F1B2D]">
                    {row.acrylic}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        <div className="mt-16 flex flex-col md:flex-row items-center justify-between gap-8 border-t border-[#0F1B2D]/10 pt-12">
          <div className="max-w-2xl">
            <h3 className="font-default font-bold text-2xl text-[#0F1B2D] uppercase tracking-tight">
              Need a custom material specification?
            </h3>
            <p className="mt-2 text-[#0F1B2D]/80">
              We provide MIL-SPEC documentation and material safety data sheets for all industrial identification projects.
            </p>
          </div>
          <Button asChild className="h-14 px-10 rounded-lg bg-[#0F1B2D] text-white hover:bg-[#0F1B2D]/90 font-mono text-sm tracking-widest uppercase shrink-0">
            <Link to="/ContactPage">CONTACT US</Link>
          </Button>
        </div>
      </div>
    </section>;
}